import { createApi } from "@reduxjs/toolkit/query/react";

import ENDPOINTS from "../../lib/api/endpoints";
import { axiosBaseQuery } from "../../lib/api/rtkBaseQuery";
import { normalizePage } from "../orders/ordersMappers";
import { mapUnreadCount, toNotification } from "./notificationsMappers";

export const notificationsApi = createApi({
  reducerPath: "notificationsApi",
  baseQuery: axiosBaseQuery(),
  tagTypes: ["Notification"],
  keepUnusedDataFor: 60,
  endpoints: (builder) => ({
    getNotifications: builder.query({
      query: ({ size }) => ({
        url: ENDPOINTS.NOTIFICATIONS.feed,
        params: { page: 0, size, sort: "id,desc" },
      }),
      transformResponse: (dto) => {
        const page = normalizePage(dto);

        return { ...page, items: page.items.map(toNotification).filter(Boolean) };
      },
      providesTags: [{ type: "Notification", id: "LIST" }],
    }),

    getUnreadCount: builder.query({
      query: () => ({ url: ENDPOINTS.NOTIFICATIONS.unreadCount }),
      transformResponse: mapUnreadCount,
      providesTags: [{ type: "Notification", id: "COUNT" }],
    }),

    markRead: builder.mutation({
      query: (id) => ({
        url: ENDPOINTS.NOTIFICATIONS.markRead(id),
        method: "PATCH",
      }),
      async onQueryStarted(id, { dispatch, queryFulfilled, getState }) {
        const feeds = notificationsApi.util.selectInvalidatedBy(getState(), [
          { type: "Notification", id: "LIST" },
        ]);

        let wasUnread = false;
        const patches = [];

        feeds.forEach(({ endpointName, originalArgs }) => {
          if (endpointName !== "getNotifications") return;

          patches.push(
            dispatch(
              notificationsApi.util.updateQueryData(
                "getNotifications",
                originalArgs,
                (draft) => {
                  const row = draft.items.find((item) => item.id === id);

                  if (!row || row.read) return;

                  row.read = true;
                  wasUnread = true;
                }
              )
            )
          );
        });

        if (wasUnread) {
          patches.push(
            dispatch(
              notificationsApi.util.updateQueryData(
                "getUnreadCount",
                undefined,
                (draft) => Math.max(0, draft - 1)
              )
            )
          );
        }

        try {
          await queryFulfilled;
        } catch {
          patches.forEach((patch) => patch.undo());
        }
      },
    }),
  }),
});

export const {
  useGetNotificationsQuery,
  useGetUnreadCountQuery,
  useMarkReadMutation,
} = notificationsApi;
